// App-wide overlay hosts: right-click context menus and toasts.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { X } from '@/components/ui/icons';

// ---------------------------------------------------------------------------
// Context menu

interface ContextMenuState {
  x: number;
  y: number;
  render: ((close: () => void) => ReactNode) | null;
}

const useContextMenuStore = create<ContextMenuState>(() => ({ x: 0, y: 0, render: null }));

export function openContextMenu(e: React.MouseEvent | MouseEvent, render: (close: () => void) => ReactNode) {
  e.preventDefault();
  e.stopPropagation();
  useContextMenuStore.setState({ x: e.clientX, y: e.clientY, render });
}

export function closeContextMenu() {
  useContextMenuStore.setState({ render: null });
}

function ContextMenuHost() {
  const { x, y, render } = useContextMenuStore();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  useLayoutEffect(() => {
    if (!render || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({
      x: Math.min(x, window.innerWidth - r.width - 8),
      y: Math.min(y, window.innerHeight - r.height - 8),
    });
  }, [x, y, render]);
  useEffect(() => {
    if (!render) return;
    const close = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      // clicks inside submenus opened from the context menu are portaled elsewhere
      if (e.target instanceof Element && e.target.closest('.popover')) return;
      closeContextMenu();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeContextMenu();
    window.addEventListener('mousedown', close, true);
    window.addEventListener('wheel', close, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', closeContextMenu);
    return () => {
      window.removeEventListener('mousedown', close, true);
      window.removeEventListener('wheel', close, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', closeContextMenu);
    };
  }, [render]);
  if (!render) return null;
  return createPortal(
    <div ref={ref} className="popover context-menu" style={{ position: 'fixed', left: pos.x, top: pos.y }} onContextMenu={(e) => e.preventDefault()}>
      {render(closeContextMenu)}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Toasts

interface Toast {
  id: number;
  message: ReactNode;
  action?: { label: string; onClick: () => void };
}

const useToastStore = create<{ toasts: Toast[] }>(() => ({ toasts: [] }));
let toastSeq = 1;

export function toast(message: ReactNode, action?: Toast['action'], ms = 4000) {
  const id = toastSeq++;
  useToastStore.setState((s) => ({ toasts: [...s.toasts, { id, message, action }] }));
  setTimeout(() => dismissToast(id), ms);
}

function dismissToast(id: number) {
  useToastStore.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
}

function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  return createPortal(
    <div className="toast-host">
      {toasts.map((t) => (
        <div key={t.id} className="toast">
          <span className="grow">{t.message}</span>
          {t.action && (
            <button
              className="toast-action"
              onClick={() => {
                t.action!.onClick();
                dismissToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button className="icon-btn xs" onClick={() => dismissToast(t.id)}>
            <X size={14} />
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}

export function OverlayHosts() {
  return (
    <>
      <ContextMenuHost />
      <ToastHost />
    </>
  );
}
